import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@aaas/shared';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import pipelineService from '../services/pipeline.service';
import type { Pipeline, PipelineCreateInput, PipelineUpdateInput } from '../types';
import { RoundFormItem, type RoundFormData } from './RoundFormItem';

interface PipelineFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  pipeline?: Pipeline | null;
  onSuccess: () => void;
}

export function PipelineFormDialog({
  open,
  onClose,
  mode,
  pipeline,
  onSuccess,
}: PipelineFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const [name, setName] = useState('');
  const [rounds, setRounds] = useState<RoundFormData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reset form when dialog opens or pipeline changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && pipeline) {
        setName(pipeline.name);
        setRounds([]); // Edit mode doesn't show rounds section
      } else {
        setName('');
        // Initialize with default rounds for create mode
        setRounds([
          {
            id: crypto.randomUUID(),
            name: t('pipeline.detail.applicationReceived'),
            round_type: 'application_received',
            isDefault: true,
          },
          {
            id: crypto.randomUUID(),
            name: t('pipeline.detail.hired'),
            round_type: 'hired',
            isDefault: true,
          },
        ]);
      }
      setError(null);
    }
  }, [open, mode, pipeline, t]);

  // Round management functions
  const handleAddRound = () => {
    const newRound: RoundFormData = {
      id: crypto.randomUUID(),
      name: '',
      round_type: '',
      isDefault: false,
    };

    // Insert new round before the last item (HIRED)
    // APPLICATION_RECEIVED should always be first, HIRED should always be last
    const updatedRounds = [...rounds];
    updatedRounds.splice(rounds.length - 1, 0, newRound); // Insert before last item
    setRounds(updatedRounds);
  };

  const handleUpdateRound = (id: string, field: 'name' | 'round_type', value: string) => {
    setRounds(rounds.map((round) => (round.id === id ? { ...round, [field]: value } : round)));
  };

  const handleDeleteRound = (id: string) => {
    // Prevent deletion of default rounds
    const roundToDelete = rounds.find((r) => r.id === id);
    if (roundToDelete?.isDefault) return;

    setRounds(rounds.filter((round) => round.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rounds.findIndex((r) => r.id === active.id);
      const newIndex = rounds.findIndex((r) => r.id === over.id);

      const draggedRound = rounds[oldIndex];

      // Prevent moving default rounds (APPLICATION_RECEIVED must stay first, HIRED must stay last)
      if (draggedRound.isDefault) {
        return; // Don't allow dragging default rounds
      }

      // Prevent dropping in first position (reserved for APPLICATION_RECEIVED)
      if (newIndex === 0) {
        return;
      }

      // Prevent dropping in last position (reserved for HIRED)
      if (newIndex === rounds.length - 1) {
        return;
      }

      setRounds(arrayMove(rounds, oldIndex, newIndex));
    }
  };

  const validateRounds = (): boolean => {
    if (mode === 'create' && rounds.length === 0) {
      setError(t('pipeline.form.errors.atLeastOneRound'));
      return false;
    }

    for (let i = 0; i < rounds.length; i++) {
      const round = rounds[i];
      if (!round.name.trim()) {
        setError(t('pipeline.form.errors.roundNameRequired', { index: i + 1 }));
        return false;
      }
      if (!round.round_type) {
        setError(t('pipeline.form.errors.roundTypeRequired', { index: i + 1 }));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }

    if (mode === 'create' && !validateRounds()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: PipelineCreateInput = {
          name: name.trim(),
          rounds: rounds.map((round) => ({
            name: round.name.trim(),
            round_type: round.round_type,
          })),
        };
        await pipelineService.createPipeline(currentWorkspace.id, token, data);
      } else if (pipeline) {
        const data: PipelineUpdateInput = {
          name: name.trim(),
        };
        await pipelineService.updatePipeline(pipeline.id, currentWorkspace.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={mode === 'create' ? 'sm:max-w-[800px]' : 'sm:max-w-[425px]'}>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('pipeline.form.createTitle') : t('pipeline.form.editTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('pipeline.form.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('pipeline.form.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {/* Rounds Section - Only show in create mode */}
          {mode === 'create' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>{t('pipeline.form.recruitmentRoundsLabel')}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500 cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">
                          <strong>{t('pipeline.form.tooltip.defaultsTitle')}</strong>
                        </p>
                        <ul className="text-xs mt-1 space-y-1">
                          <li>• <strong>{t('pipeline.detail.applicationReceived')}</strong> - {t('pipeline.form.tooltip.applicationReceived')}</li>
                          <li>• <strong>{t('pipeline.detail.hired')}</strong> - {t('pipeline.form.tooltip.hired')}</li>
                        </ul>
                        <p className="text-xs mt-2 text-gray-500">
                          {t('pipeline.form.tooltip.addCustom')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddRound}
                  disabled={isSubmitting}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('pipeline.form.addRound')}
                </Button>
              </div>

              {rounds.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-sm text-gray-500">{t('pipeline.form.roundsEmpty')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('pipeline.form.roundsEmptyHint')}</p>
                </div>
              ) : (
                <div>
                  {/* Header Row */}
                  <div className="flex items-center gap-3 px-4 pb-2 mb-1">
                    <div className="w-6"></div> {/* Drag handle space */}
                    <div className="w-8"></div> {/* Order number space */}
                    <div className="flex-grow grid grid-cols-2 gap-3">
                      <Label className="text-center text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('pipeline.form.headerName')}</Label>
                      <Label className="text-center text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('pipeline.form.headerType')}</Label>
                    </div>
                    <div className="w-8"></div> {/* Delete button space */}
                  </div>

                  {/* Rounds List */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={rounds.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {rounds.map((round, index) => (
                          <RoundFormItem
                            key={round.id}
                            round={round}
                            index={index}
                            onUpdate={handleUpdateRound}
                            onDelete={handleDeleteRound}
                            disabled={isSubmitting}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('common.creating') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('pipeline.form.createButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
